/**
 * Usage (UBB) tab (#1122, #1123): last month's Pax8 arrears per customer × usage family (Azure, Axcient, CrowdStrike,
 * Proofpoint, LastPass, Veeam) against what Books billed. Shows cost, MSRP (only where Pax8 has one), the expected
 * bill from the family's markup rule (default MSRP, #1150), what was billed and the margin. Pooled usage on Ewee's own
 * Pax8 company is shown apart until its seat map is confirmed (#1151–#1154).
 */
import { EmptyState, Section } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import type { ReconLineOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { Tile } from '../profit/format'
import { QueryView } from '../profit/states'
import {
  BandValue,
  bandOf,
  cad,
  customerLabel,
  DASH_CELL,
  ExtLink,
  FilterChips,
  type GroupedColumn,
  GroupedTable,
  Legend,
  MarginValue,
  MsrpValue,
  marginPct,
  msrpDelta,
  num,
  pax8Url,
  SearchBox,
  signed,
  TONE,
  usageQuery,
} from './common'
import { matchesSearch, total } from './licences-tab'
import { NotesButton } from './notes'

type UsageFilter = 'all' | 'negative' | 'under_10' | 'under_20' | 'msrp' | 'not_billed' | 'below_expected' | 'noted'

const billed = (r: ReconLineOut) => num(r.usage_billed)
const cost = (r: ReconLineOut) => num(r.usage_cost)
const margin = (r: ReconLineOut) => num(r.margin_pct_now) ?? marginPct(r.usage_billed, r.usage_cost)
const isNotBilled = (r: ReconLineOut) => billed(r) === null || (r.flags ?? []).includes('not_billed')
const belowExpected = (r: ReconLineOut) => {
  const e = num(r.expected_rate)
  const b = billed(r)
  return e !== null && b !== null && b < e - 0.005
}

const FILTERS: { id: UsageFilter; label: string; test: (r: ReconLineOut) => boolean }[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'negative', label: 'Negative', test: (r) => (margin(r) ?? 0) < 0 },
  { id: 'under_10', label: 'Under 10%', test: (r) => ((m) => m !== null && m >= 0 && m < 10)(margin(r)) },
  { id: 'under_20', label: 'Under 20%', test: (r) => ((m) => m !== null && m >= 10 && m < 20)(margin(r)) },
  { id: 'msrp', label: 'Under MSRP', test: (r) => ((d) => d !== null && d < 0)(msrpDelta(r.msrp_delta_pct, r.usage_billed, r.usage_msrp)) },
  { id: 'below_expected', label: 'Below expected', test: belowExpected },
  { id: 'not_billed', label: 'Not billed', test: isNotBilled },
  { id: 'noted', label: 'Has a note', test: (r) => (r.notes_count ?? 0) > 0 },
]

const familyLabel = (r: ReconLineOut) => r.usage_family ?? r.product_name ?? 'Usage'

function usageColumns(pooled: boolean): GroupedColumn<ReconLineOut>[] {
  const notes: GroupedColumn<ReconLineOut> = {
    id: 'notes',
    header: 'Notes',
    group: ' ',
    sortValue: (r) => r.notes_count ?? 0,
    cell: (r) => (
      <NotesButton lineKey={r.line_key} count={r.notes_count ?? 0} title={`${customerLabel(r)} · ${familyLabel(r)}`} subtitle="Usage" />
    ),
  }
  const head: GroupedColumn<ReconLineOut>[] = [
    {
      id: 'customer',
      header: pooled ? 'Pax8 company' : 'Customer',
      group: '',
      sortValue: customerLabel,
      cell: (r) => (
        <ExtLink href={r.links?.crm} className="font-medium">
          {customerLabel(r)}
        </ExtLink>
      ),
    },
    {
      id: 'family',
      header: 'Usage',
      group: '',
      sortValue: familyLabel,
      cell: (r) => (
        <div className="min-w-36">
          <ExtLink href={pax8Url(r.pax8_company_id, r.links?.pax8)}>{familyLabel(r)}</ExtLink>
          {r.books_lines?.length ? (
            <span className="block text-xs text-muted-foreground">
              Books:{' '}
              {r.books_lines
                .map((b) => b.item_name ?? b.sku_raw)
                .filter(Boolean)
                .join(', ')}
            </span>
          ) : null}
        </div>
      ),
    },
  ]
  if (pooled) {
    return [
      ...head,
      { id: 'cost', header: 'Pax8 cost', group: 'Cost', numeric: true, cell: (r) => cad(r.usage_cost), sortValue: cost },
      { id: 'msrp', header: 'MSRP', group: 'Cost', numeric: true, cell: (r) => cad(r.usage_msrp), sortValue: (r) => num(r.usage_msrp) },
      notes,
    ]
  }
  return [
    ...head,
    {
      id: 'billed',
      header: 'We bill',
      group: 'Price vs MSRP',
      numeric: true,
      sortValue: billed,
      cell: (r) =>
        isNotBilled(r) ? (
          <span className="font-sans font-medium text-destructive">not billed</span>
        ) : (
          <ExtLink href={r.links?.books_invoice}>{cad(r.usage_billed)}</ExtLink>
        ),
    },
    {
      id: 'msrp',
      header: 'MSRP',
      group: 'Price vs MSRP',
      numeric: true,
      cell: (r) => cad(r.usage_msrp),
      sortValue: (r) => num(r.usage_msrp),
    },
    {
      id: 'msrp_delta',
      header: '± MSRP',
      group: 'Price vs MSRP',
      numeric: true,
      sortValue: (r) => msrpDelta(r.msrp_delta_pct, r.usage_billed, r.usage_msrp),
      cell: (r) =>
        isNotBilled(r) ? (
          DASH_CELL
        ) : (
          <MsrpValue server={r.msrp_delta_pct} billed={r.usage_billed} msrp={r.usage_msrp} unit="for the month" />
        ),
    },
    { id: 'cost', header: 'Pax8 cost', group: 'Margin', numeric: true, cell: (r) => cad(r.usage_cost), sortValue: cost },
    {
      id: 'expected',
      header: 'Expected',
      group: 'Margin',
      numeric: true,
      sortValue: (r) => num(r.expected_rate),
      cell: (r) => {
        const e = num(r.expected_rate)
        if (e === null) return DASH_CELL
        const b = billed(r)
        const gap = b === null ? -e : b - e
        const tone = Math.abs(gap) < 0.005 ? TONE.ok : gap < 0 ? TONE.warn : TONE.quiet
        return (
          <BandValue
            text={cad(e)}
            tone={tone}
            tip={[
              'Expected bill from the family’s markup rule (default: MSRP)',
              b === null ? 'Nothing billed yet' : `We billed ${cad(b)}, ${signed(gap, cad)} against expected`,
            ]}
            testId="usage-expected"
          />
        )
      },
    },
    {
      id: 'margin_amt',
      header: 'Margin',
      group: 'Margin',
      numeric: true,
      sortValue: (r) => ((b, c) => (b === null || c === null ? null : b - c))(billed(r), cost(r)),
      cell: (r) => {
        const b = billed(r)
        const c = cost(r)
        if (b === null || c === null) return DASH_CELL
        return <span className={b - c < 0 ? 'text-destructive' : undefined}>{signed(b - c, cad)}</span>
      },
    },
    {
      id: 'margin_pct',
      header: 'Margin %',
      group: 'Margin',
      numeric: true,
      sortValue: margin,
      cell: (r) =>
        isNotBilled(r) ? (
          DASH_CELL
        ) : (
          <MarginValue
            when="Last month"
            billed={r.usage_billed}
            cost={r.usage_cost}
            margin={r.margin_pct_now}
            band={r.band_now}
            unit="for the month"
          />
        ),
    },
    notes,
  ]
}

function Stats({ lines }: { lines: ReconLineOut[] }) {
  const billedRows = lines.filter((r) => !isNotBilled(r))
  const c = total(lines, cost)
  const b = total(billedRows, billed)
  const bc = total(billedRows, cost)
  const m = b && bc !== null ? ((b - bc) / b) * 100 : null
  const neg = lines.filter((r) => bandOf(r.band_now, margin(r)) === 'negative').length
  const unbilled = lines.filter(isNotBilled)
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
      <Tile label="Usage cost (last invoice)" value={cad(c)} />
      <Tile label="Billed where matched" value={cad(b)} />
      <Tile label="Margin where billed" value={m === null ? '—' : `${m.toFixed(1)}%`} tone={m !== null && m < 0 ? 'danger' : undefined} />
      <Tile label="Negative" value={c === null ? '—' : neg} tone={neg ? 'danger' : undefined} />
      <Tile label="Not billed" value={unbilled.length} tone={unbilled.length ? 'warning' : undefined} />
      <Tile label="Unbilled cost" value={cad(total(unbilled, cost))} tone={unbilled.length ? 'warning' : undefined} />
    </div>
  )
}

export function UsageTab() {
  const q = useQuery(usageQuery())
  const [filter, setFilter] = React.useState<UsageFilter>('all')
  const [search, setSearch] = React.useState('')
  const cols = React.useMemo(() => usageColumns(false), [])
  const pooledCols = React.useMemo(() => usageColumns(true), [])
  return (
    <QueryView query={q} what="Usage">
      {(data) => {
        const lines = data.lines ?? []
        const pooled = data.pooled ?? []
        if (!data.period) return <EmptyState title="No Pax8 usage invoice has been read yet." />
        const test = FILTERS.find((f) => f.id === filter)?.test ?? (() => true)
        const rows = lines.filter((r) => test(r) && matchesSearch(r, search))
        const period = data.period.slice(0, 7)
        const month = `${formatWhen(`${period}-01T12:00:00`, { style: 'date' }).split(' ')[0]} ${period.slice(0, 4)}`
        return (
          <div className="space-y-3.5">
            <p className="text-sm text-muted-foreground">Arrears for {month}, from the most recent Pax8 invoice.</p>
            <Stats lines={lines} />
            <FilterChips
              label="Usage filters"
              value={filter}
              onChange={setFilter}
              options={FILTERS.map((f) => ({ id: f.id, label: f.label, count: lines.filter(f.test).length }))}
            />
            <SearchBox
              value={search}
              onChange={setSearch}
              label="Search customer, usage"
              count={`${rows.length} of ${lines.length} lines`}
            />
            <Legend quantity={false} />
            <GroupedTable
              label="Usage"
              columns={cols}
              rows={rows}
              rowKey={(r) => r.line_key}
              initialSort={{ id: 'margin_pct', dir: 1 }}
              empty={<EmptyState title="No usage line matches this filter." />}
            />
            <p className="text-xs text-muted-foreground">
              MSRP is Pax8’s retail price on each usage line. Where retail equals our cost there is no MSRP, and that part is left out of
              the comparison.
            </p>
            <Section title="Pooled under Ewee" count={pooled.length} collapsible defaultOpen={pooled.length > 0}>
              <p className="mb-2 text-sm text-muted-foreground">
                Usage billed to Ewee’s own Pax8 company (Proofpoint, Axcient, Veeam, LastPass). It is allocated to customers once their seat
                map is confirmed, so it is not compared with Books here.
              </p>
              <GroupedTable
                label="Pooled usage"
                columns={pooledCols}
                rows={pooled}
                rowKey={(r) => r.line_key}
                empty={<EmptyState title="No pooled usage on the last invoice." />}
              />
            </Section>
          </div>
        )
      }}
    </QueryView>
  )
}
